import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { WHATS_NEW_ART } from "./WhatsNewArt";
import { WHATS_NEW_COPY, WHATS_NEW_SCENES } from "./whatsNewRelease";
import "./whatsNew.css";

const FOCUSABLE = "button, [href], [tabindex]:not([tabindex='-1'])";

/**
 * Keep each miniature inside its slot. The slot height is whatever remains
 * after the copy, and the illustrations are not all the same height. Zoom
 * scales a too-tall miniature down as a whole; it never scales one up, and
 * it does not change the copy, the kicker, or the actions.
 */
let fittingWhatsNewArt = false;

function fitWhatsNewArt(root) {
  if (!root || fittingWhatsNewArt) return;
  fittingWhatsNewArt = true;
  try {
    fitWhatsNewArtNow(root);
  } finally {
    fittingWhatsNewArt = false;
  }
}

function fitWhatsNewArtNow(root) {
  root.querySelectorAll(".wn-scene__art").forEach((slot) => {
    const art = slot.firstElementChild;
    if (!art || !art.classList.contains("wn-art")) return;
    art.style.zoom = "1";
    const available = slot.clientHeight;
    if (!available) return;
    let top = Infinity;
    let bottom = -Infinity;
    const nodes = [art, ...art.querySelectorAll("*")];
    nodes.forEach((el) => {
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") return;
      const rect = el.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      if (rect.top < top) top = rect.top;
      if (rect.bottom > bottom) bottom = rect.bottom;
    });
    const needed = bottom - top;
    if (!Number.isFinite(needed) || needed <= 0) return;
    const scale = Math.min(1, (available - 1) / needed);
    art.style.zoom = scale < 0.995 ? String(Math.round(scale * 1000) / 1000) : "1";
  });
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function Title({ scene }) {
  const idx = scene.title.indexOf(scene.accent);
  if (idx < 0) {
    return scene.title;
  }
  return (
    <>
      {scene.title.slice(0, idx)}
      <em>{scene.accent}</em>
    </>
  );
}

/**
 * Full-screen, swipeable launch moment. Native scroll-snap handles swiping
 * (touch, trackpad, keyboard); state only tracks which scene is active so its
 * miniature UI can play. onClose() is called when the user leaves it.
 */
export function WhatsNewExperience({ onClose, dontShowAgain = false, onDontShowAgainChange }) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const frameRef = useRef(0);
  const [active, setActive] = useState(0);
  const [seen, setSeen] = useState(() => new Set([0]));
  const last = WHATS_NEW_SCENES.length - 1;
  const isLast = active === last;

  const close = useCallback(() => onClose?.(), [onClose]);

  useHardwareBackHandler(() => {
    close();
    return true;
  }, true);

  const goTo = useCallback((index) => {
    const track = trackRef.current;
    const next = Math.max(0, Math.min(last, index));
    if (track && typeof track.scrollTo === "function") {
      track.scrollTo({
        left: next * track.clientWidth,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    }
    setActive(next);
    setSeen((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
  }, [last]);

  const handleScroll = useCallback(() => {
    if (frameRef.current) {
      return;
    }
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = 0;
      const track = trackRef.current;
      const root = rootRef.current;
      if (!track || !root || !track.clientWidth) {
        return;
      }
      const progress = track.scrollLeft / track.clientWidth;
      root.style.setProperty("--wn-p", progress.toFixed(3));
      const index = Math.max(0, Math.min(last, Math.round(progress)));
      setActive((prev) => (prev === index ? prev : index));
      setSeen((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    });
  }, [last]);

  const handleNext = () => {
    if (isLast) {
      close();
      return;
    }
    goTo(active + 1);
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver !== "function") {
      fitWhatsNewArt(root);
      return undefined;
    }
    fitWhatsNewArt(root);
    const observer = new ResizeObserver(() => fitWhatsNewArt(root));
    root.querySelectorAll(".wn-scene__art").forEach((slot) => observer.observe(slot));
    return () => observer.disconnect();
  }, []);

  // Focus, scroll lock, focus restore.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    rootRef.current?.querySelector(".wn-cta")?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goTo(active + 1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goTo(active - 1);
      return;
    }
    if (event.key === "Tab") {
      const nodes = Array.from(rootRef.current?.querySelectorAll(FOCUSABLE) || []);
      if (!nodes.length) {
        return;
      }
      const first = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className="wn"
      role="dialog"
      aria-modal="true"
      aria-label={WHATS_NEW_COPY.dialogLabel}
      data-testid="whats-new"
      data-active-scene={WHATS_NEW_SCENES[active].id}
      onKeyDown={handleKeyDown}
    >
      <div className="wn__stage">
        <div className="wn__backdrop" aria-hidden="true">
          <i className="wn__orb wn__orb--gold" />
          <i className="wn__orb wn__orb--blue" />
          <i className="wn__orb wn__orb--cyan" />
          <i className="wn__grid" />
        </div>

        <header className="wn__top">
          <span className="wn__brand">
            <i className="wn__brand-dot" aria-hidden="true" />
            CrewPilot
          </span>
          <button
            type="button"
            className="wn__skip"
            onClick={() => close()}
          >
            {WHATS_NEW_COPY.skip}
          </button>
        </header>

        <div className="wn__track" ref={trackRef} onScroll={handleScroll}>
          {WHATS_NEW_SCENES.map((scene, index) => {
            const Art = WHATS_NEW_ART[scene.id];
            const state = index === active ? "is-active" : seen.has(index) ? "is-seen" : "";
            return (
              <section
                key={scene.id}
                className={`wn-scene wn-scene--${scene.id} ${state}`}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${WHATS_NEW_SCENES.length}`}
                aria-hidden={index === active ? undefined : "true"}
              >
                <div className="wn-scene__art" aria-hidden="true">
                  <Art />
                </div>
                <div className="wn-scene__copy">
                  <p className="wn-eyebrow">{scene.eyebrow}</p>
                  <h2 className="wn-title">
                    <Title scene={scene} />
                  </h2>
                  <p className="wn-body">{scene.body}</p>
                  {scene.footnote ? <p className="wn-foot">{scene.footnote}</p> : null}
                </div>
              </section>
            );
          })}
        </div>

        <footer className="wn__bottom">
          {onDontShowAgainChange ? (
            <label className="wn-dsa">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(event) => onDontShowAgainChange(event.target.checked)}
              />
              <span className="wn-dsa__box" aria-hidden="true" />
              <span className="wn-dsa__text">{WHATS_NEW_COPY.dontShowAgain}</span>
              <span className="wn-dsa__hint">{WHATS_NEW_COPY.dontShowAgainHint}</span>
            </label>
          ) : null}
          <div className="wn-dots" role="group" aria-label="Choose a screen">
            {WHATS_NEW_SCENES.map((scene, index) => (
              <button
                key={scene.id}
                type="button"
                className={`wn-dot${index === active ? " is-active" : ""}`}
                aria-label={`${index + 1} of ${WHATS_NEW_SCENES.length}: ${scene.nav}`}
                aria-current={index === active ? "step" : undefined}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
          <button type="button" className="wn-cta" onClick={handleNext}>
            <span>{isLast ? WHATS_NEW_COPY.finish : WHATS_NEW_COPY.next}</span>
          </button>
          <p className={`wn-replay${isLast ? " is-visible" : ""}`} aria-hidden={!isLast}>
            {WHATS_NEW_COPY.replayHint}
          </p>
        </footer>
      </div>
    </div>
  );
}
